import React, { useEffect, useState, useCallback, useMemo } from "react";
import Style from "../style/bloglanding.module.scss";
import { useAppDispatch } from "../redux/hooks";
import { fetchBlogs } from "../redux/Slice";
import { useParams } from "react-router-dom";
import { saveBlogsToDB, getBlogsFromDB } from "../indexDb/blogDb";
import Loader from "../Loader/Loader";

const BlogLanding = () => {
  const { userTitle, userCategory, userId } = useParams();
  const dispatch = useAppDispatch();
  const [blogs, setBlogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);
  const [search, setSearch] = useState({ title: "", category: "" });
  const baseUrl = "https://backend.onedialusa.com/assets/img/blogs/";

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const loadBlogs = useCallback(async () => {
    setIsLoading(true);

    try {
      const cachedBlogs = await getBlogsFromDB();
      if (cachedBlogs.length > 0) {
        setBlogs(cachedBlogs);
        setIsLoading(false);
        return;
      }

      let allBlogs: any[] = [];
      let page = 1;
      let foundBlog = null;

      while (true) {
        const response = await dispatch(fetchBlogs({ page })).unwrap();
        const newBlogs = response?.blogs?.blogs?.data || [];
        if (!newBlogs.length) break;
        allBlogs = [...allBlogs, ...newBlogs];
        if (userId) {
          foundBlog = allBlogs.find((blog) => blog.id === parseInt(userId));
        }

        if (foundBlog) break;

        page += 1;
      }

      if (foundBlog) {
        setBlogs([foundBlog]);
      } else {
        setBlogs(allBlogs);
      }

      await saveBlogsToDB(allBlogs);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, userId]);

  useEffect(() => {
    loadBlogs();
  }, [loadBlogs]);
  const filteredBlogs = useMemo(() => {
    let filtered = [...blogs];
    if (userTitle) {
      filtered = filtered.filter(
        (item) =>
          item.title?.toLowerCase().replaceAll(" ", "-") ===
          userTitle?.toLowerCase()
      );
    }
    if (userId) {
      filtered = filtered.filter((item) => item.id === parseInt(userId));
    }
    if (userCategory) {
      filtered = filtered.filter(
        (item) =>
          item.categoryName?.toLowerCase().replaceAll(" ", "-") ===
          userCategory?.toLowerCase()
      );
    }
    return filtered;
  }, [blogs, userTitle, userCategory, userId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.title) {
      window.location.pathname = `/blog/${search.title
        .toLowerCase()
        .replaceAll(" ", "-")}`;
    } else if (search.category) {
      window.location.pathname = `/blog/category/${search.category
        .toLowerCase()
        .replaceAll(" ", "-")}`;
    }
  };

  if (showLoader || (isLoading && blogs.length === 0)) {
    return <Loader />;
  }

  return (
    <div className={Style.container}>
      <div className={Style.title}>
        {filteredBlogs.length > 0 ? (
          <h1>{filteredBlogs[0].title}</h1>
        ) : (
          <h1>No blog found</h1>
        )}
      </div>

      <div className={Style.wrapper}>
        <div className={Style.divide}>
          {filteredBlogs.length > 0 ? (
            <div className={Style.leftAside}>
              <span>
                <img
                  src={`${baseUrl}${filteredBlogs[0].image}`}
                  onError={(e: any) => {
                    e.target.src =
                      "https://images.unsplash.com/photo-1499750310107-5fef28a66643?fm=jpg&q=60&w=3000";
                  }}
                  alt={filteredBlogs[0].title}
                  loading="lazy"
                  width="100%"
                  height="auto"
                  style={{ objectFit: "cover" }}
                />
              </span>

              <div className={Style.detail}>
                <ul>
                  <li>
                    <i className="fa-solid fa-user"></i> Admin
                  </li>
                  <li>
                    <i className="fa-solid fa-calendar"></i>{" "}
                    {filteredBlogs[0].created_at}
                  </li>
                  <li>
                    <i className="fa-solid fa-tag"></i>{" "}
                    {filteredBlogs[0].categoryName}
                  </li>
                </ul>
              </div>

              <div className={Style.fullData}>
                <h2>{filteredBlogs[0].title}</h2>
                <div className={Style.shareContainer}>
                  <div className={Style.navButtons}>
                    <button className={Style.prevNext}>← Previous</button>
                    <button className={Style.prevNext}>Next →</button>
                  </div>
                </div>
              </div>

              <div
                className={Style.Description}
                dangerouslySetInnerHTML={{
                  __html: filteredBlogs[0].content,
                }}
              />
            </div>
          ) : (
            <p>No blog found.</p>
          )}

          <div className={Style.rightAside}>
            <div className={Style.searchBar}>
              <h2>Search Blogs</h2>
              <form onSubmit={handleSubmit}>
                <input
                  type="text"
                  placeholder="Search by title..."
                  value={search.title}
                  onChange={(e) =>
                    setSearch((prev) => ({ ...prev, title: e.target.value }))
                  }
                />
                <input
                  type="text"
                  placeholder="Search by category..."
                  value={search.category}
                  onChange={(e) =>
                    setSearch((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                />
                <button>Search</button>
              </form>
            </div>

            <div className={Style.category}>
              <h2>Categories</h2>
              <ul>
                {Object.entries(
                  filteredBlogs.reduce((acc: Record<string, number>, blog) => {
                    if (blog.categoryName) {
                      acc[blog.categoryName] =
                        (acc[blog.categoryName] || 0) + 1;
                    }
                    return acc;
                  }, {} as Record<string, number>)
                ).map(([category, count]) => (
                  <li key={category}>
                    <a
                      href={`/blog/category/${category
                        .toLowerCase()
                        .replaceAll(" ", "-")}`}
                    >
                      {category} ({count})
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogLanding;
